import React, {useContext, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import CancelIcon from '@mui/icons-material/Cancel';
import {Container} from '@mui/system';
import {Box, Button, Chip, FormControl, InputLabel, MenuItem, OutlinedInput, Select, TextField, Alert, Stack, Typography} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import {deleteDoctor, fetchOneDoctor, fetchSpecializations, postDoctor} from '../http/doctorAPI';
import {fetchDoctorCategories} from '../http/categoryAPI';
import MySnackbar from '../components/MySnackbar';
import {getBranches} from '../http/clinicAPI';
import {DOCTORS_ROUTE} from '../utils/consts';
import useSnackbar from '../hooks/useSnackbar';
import {Context} from '..';
import {getImageUrl} from "../helpers/getImageUrl";
import { validateImageFile, ALLOWED_IMAGE_ACCEPT } from '../helpers/imageUpload';
import useUnsavedChangesWarning from "../hooks/useUnsavedChangesWarning";

const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: 48 * 4.5 + 8,
            width: 250,
        },
    },
};

const Doctor = () => {
    const params = useParams();
    const navigate = useNavigate();
    const {user} = useContext(Context);

    const [doctor, setDoctor] = useState({});
    const [image, setImage] = useState(null);
    const [doctorImage, setDoctorImage] = useState(null);

    const [branches, setBranches] = useState([]);
    const [selectedBranchesNames, setSelectedBranchesNames] = useState([]);

    const [specializations, setSpecializations] = useState([]);
    const [selectedSpecializationsNames, setSelectedSpecializationsNames] = useState([]);

    const [categories, setCategories] = useState([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);

    const [linkError, setLinkError] = useState(false);
    const [appointmentLinkError, setAppointmentLinkError] = useState(false);
    const [hasUnsaved, setHasUnsaved] = useState(false);
    
    const [isLoading, setIsLoading] = useState(true);

    const snack = useSnackbar(false, 'Ошибка');
    const isNew = params.id === 'new';
    const pageTitle = isNew ? 'Создание врача' : 'Редактирование врача';
    const { confirmLeave } = useUnsavedChangesWarning(hasUnsaved);

    // Функция валидации URL
    const isValidUrl = (string) => {
        if (!string || string.trim() === '') return true; // Пустая строка валидна
        try {
            const url = new URL(string);
            return url.protocol === 'http:' || url.protocol === 'https:';
        } catch (_) {
            return false;
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            
            try {
                const [branchesData, specializationsData, categoriesData] = await Promise.all([
                    getBranches().catch((error) => {
                        console.error('Error fetching branches:', error);
                        snack.handleChange(true, 'Ошибка загрузки филиалов');
                        return [];
                    }),
                    fetchSpecializations().catch((error) => {
                        console.error('Error fetching specializations:', error);
                        snack.handleChange(true, 'Ошибка загрузки специализаций');
                        return [];
                    }),
                    fetchDoctorCategories().catch((error) => {
                        console.error('Error fetching categories:', error);
                        snack.handleChange(true, 'Ошибка загрузки категорий');
                        return [];
                    })
                ]);

                setBranches(branchesData);
                setSpecializations(specializationsData);
                setCategories(categoriesData);

                if (params.id !== 'new') {
                    const doctorData = await fetchOneDoctor(params.id);
                    setDoctor(doctorData);
                    if (doctorData.image) {
                        setImage(getImageUrl(doctorData.image));
                    }
                    setSelectedBranchesNames(doctorData.branches?.map((branch) => branch.name) || []);
                    setSelectedSpecializationsNames(
                        doctorData.specializations?.map((specialization) => specialization.name) || []
                    );
                    setSelectedCategoryIds(doctorData.categoryIds || []);
                }
                setHasUnsaved(false);
            } catch (error) {
                console.error('Error loading data:', error);
                snack.handleChange(true, 'Ошибка загрузки данных');
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [params.id]);


    // работа с фото
    const handleChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const result = validateImageFile(file);
        if (!result.valid) { snack.handleChange(true, result.error); return; }
        setImage(URL.createObjectURL(file));
        setDoctorImage(file);
        setHasUnsaved(true);
    };

    const removeDoctor = () => {
        deleteDoctor({id: doctor.id})
            .then(() => {
                setHasUnsaved(false);
                navigate(DOCTORS_ROUTE);
            })
            .catch((error) => {
                console.error('Error deleting doctor:', error);
                snack.handleChange(true, 'Ошибка удаления врача');
            });
    };

    const saveDoctor = async () => {
        const formData = new FormData();
        if (params.id !== 'new') {
            formData.append('id', params.id);
        }
        formData.append('name', doctor.name);
        formData.append('misId', doctor.misId ?? '');
        formData.append('description', doctor.description);
        formData.append('link', doctor.link ?? '');
        formData.append('appointmentLink', doctor.appointmentLink ?? '');

        const branchesMap = new Map(branches.map(b => [b.name, b]));
        const doctorBranches = selectedBranchesNames
            .map(name => branchesMap.get(name))
            .filter(Boolean);
        formData.append('branches', JSON.stringify(doctorBranches));

        const specializationsMap = new Map(specializations.map(s => [s.name, s]));
        const doctorSpecializations = selectedSpecializationsNames
            .map(name => specializationsMap.get(name))
            .filter(Boolean);
        formData.append('specializations', JSON.stringify(doctorSpecializations));

        if (doctorImage) {
            formData.append('image', doctorImage);
        }

        if (params.id !== 'new' || (selectedCategoryIds && selectedCategoryIds.length > 0)) {
            formData.append('categoryIds', JSON.stringify(selectedCategoryIds || []));
        }

        formData.append('authorId', user.user.id);

        try {
            await postDoctor(formData);
            setHasUnsaved(false);
            navigate(-1);
        } catch (error) {
            alert('Данные введены некорректно. Проверьте, нет ли дубликата с таким же МИС ID врача');
        }
    };

    function handleSelectBranch(event) {
        const {value} = event.target;
        setSelectedBranchesNames(typeof value === 'string' ? value.split(',') : value);
        setHasUnsaved(true);
    }

    function deleteSelectedBranch(branchName) {
        setSelectedBranchesNames(selectedBranchesNames.filter((branch) => branch !== branchName));
        setHasUnsaved(true);
    }

    function deleteSelectedSpecialization(specName) {
        setSelectedSpecializationsNames(selectedSpecializationsNames.filter((spec) => spec !== specName));
        setHasUnsaved(true);
    }

    function handleSelectCategories(event) {
        const {value} = event.target;
        setSelectedCategoryIds(typeof value === 'string' ? value.split(',') : value);
        setHasUnsaved(true);
    }

    function deleteSelectedCategory(categoryId) {
        setSelectedCategoryIds(selectedCategoryIds.filter((id) => id !== categoryId));
        setHasUnsaved(true);
    }

    function handleSelectSpecialization(event) {
        const {value} = event.target;
        setSelectedSpecializationsNames(typeof value === 'string' ? value.split(',') : value);
        setHasUnsaved(true);
    }

    const handleBack = () => {
        if (!confirmLeave()) {
            return;
        }
        navigate(-1);
    };

    return (
        <Container
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                flexDirection: 'column',
            }}
        >
            <MySnackbar {...snack} />
            {isLoading ? (
                <Box sx={{mt: 4, textAlign: 'center', width: '100%'}}>
                    Загрузка данных...
                </Box>
            ) : (
                <>
            <Stack spacing={1} sx={{mb: 3, width: '100%'}}>
                <Button
                    variant='text'
                    size='small'
                    startIcon={<ArrowBackIcon />}
                    sx={{alignSelf: 'flex-start'}}
                    onClick={handleBack}
                >
                    Назад
                </Button>
                <Typography variant='h4' fontWeight={600}>
                    {pageTitle}
                </Typography>
            </Stack>
            <TextField
                sx={{mb: 2}}
                label='ФИО врача'
                error={!doctor.name}
                value={doctor.name}
                onChange={(e) => {
                    if (e.target.value.length > 254) {
                        snack.handleChange(true, 'Кол-во символов превышает 255');
                    } else {
                        setDoctor({...doctor, name: e.target.value});
                        setHasUnsaved(true);
                    }
                }}
                fullWidth
                variant='outlined'
                InputLabelProps={{shrink: true}}
            />
            <TextField
                sx={{mb: 2}}
                label='ID врача в CRM (misId)'
                placeholder='Например: 1234'
                value={doctor.misId || ''}
                onChange={(e) => {
                    if (e.target.value.length > 254) {
                        snack.handleChange(true, 'Кол-во символов превышает 255');
                    } else {
                        setDoctor({...doctor, misId: e.target.value});
                        setHasUnsaved(true);
                    }
                }}
                fullWidth
                variant='outlined'
                InputLabelProps={{shrink: true}}
                helperText='Это значение можно взять из адресной строки на странице врача в CRM'
            />

            <TextField
                sx={{mb: 2}}
                label='Описание врача (до 800 символов)'
                multiline
                minRows={4}
                error={!doctor.description}
                value={doctor.description}
                onChange={(e) => {
                    if (e.target.value.length > 800) {
                        snack.handleChange(true, 'Кол-во символов не должно превышать 800');
                    } else {
                        setDoctor({...doctor, description: e.target.value});
                        setHasUnsaved(true);
                    }
                }}
                fullWidth
                InputLabelProps={{shrink: true}}
            />
            <TextField
                sx={{mb: 2}}
                label='Ссылка, по которой можно оставить отзыв на врача'
                value={doctor.link ? doctor.link : ''}
                error={linkError}
                helperText={linkError ? 'Некорректная ссылка. Укажите полный URL (например: https://example.com)' : ''}
                onChange={(e) => {
                    const value = e.target.value;
                    if (value.length > 254) {
                        snack.handleChange(true, 'Кол-во символов превышает 255');
                    } else {
                        setDoctor({...doctor, link: value});
                        setLinkError(!isValidUrl(value));
                        setHasUnsaved(true);
                    }
                }}
                fullWidth
                variant='outlined'
                InputLabelProps={{shrink: true}}
            />
            <TextField
                sx={{mb: 2}}
                label='Ссылка на подробную информацию о враче'
                value={doctor.appointmentLink ? doctor.appointmentLink : ''}
                error={appointmentLinkError}
                helperText={appointmentLinkError ? 'Некорректная ссылка. Укажите полный URL (например: https://example.com)' : ''}
                onChange={(e) => {
                    const value = e.target.value;
                    if (value.length > 254) {
                        snack.handleChange(true, 'Кол-во символов превышает 255');
                    } else {
                        setDoctor({...doctor, appointmentLink: value});
                        setAppointmentLinkError(!isValidUrl(value));
                        setHasUnsaved(true);
                    }
                }}
                fullWidth
                variant='outlined'
                InputLabelProps={{shrink: true}}
            />
            <Box>
                {image && <img width='100' src={image} alt=''/>}
            </Box>
            <Button variant='outlined' component='label' sx={{mb: 1}}>
                Выбрать фото
                <input hidden accept={ALLOWED_IMAGE_ACCEPT} type="file" onChange={handleChange}/>
            </Button>
            <FormControl sx={{mb: 1, width: 400}} error={selectedBranchesNames.length === 0}>
                <InputLabel id='branchesLabel'>Филиалы, в которых отображается врач</InputLabel>
                <Select
                    labelId='branchesLabel'
                    multiple
                    fullWidth
                    required
                    value={selectedBranchesNames}
                    onChange={handleSelectBranch}
                    input={<OutlinedInput id='select-multiple-chip' label='Филиалы, в которых отображается врач'/>}
                    renderValue={(selected) => (
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                            {selected.map((branchName) => {
                                const branch = branches?.find((branch) => branch.name === branchName);
                                return branch ? (
                                    <Chip
                                        key={branchName}
                                        label={branch.name}
                                        deleteIcon={<CancelIcon onMouseDown={(event) => event.stopPropagation()}/>}
                                        onDelete={(e) => deleteSelectedBranch(branchName)}
                                        clickable
                                    />
                                ) : null;
                            })}
                        </Box>
                    )}
                    MenuProps={MenuProps}
                >
                    {branches
                        .filter((branch) => branch.isActive !== false)
                        .map((branch) => (
                            <MenuItem key={branch.id} value={branch.name}>
                                {branch.name}
                            </MenuItem>
                        ))}
                </Select>
            </FormControl>

            <FormControl sx={{mb: 1, width: 400}} error={selectedSpecializationsNames.length === 0}>
                <InputLabel id='branchesLabel'>Специализации врача</InputLabel>
                <Select
                    labelId='branchesLabel'
                    multiple
                    fullWidth
                    required
                    value={selectedSpecializationsNames}
                    onChange={handleSelectSpecialization}
                    input={<OutlinedInput id='select-multiple-chip' label='Специализации врача'/>}
                    renderValue={(selected) => (
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                            {selected.map((specializationName) => (
                                <Chip
                                    key={specializationName}
                                    label={
                                        specializations?.find(
                                            (specialization) => specialization.name === specializationName
                                        )?.name
                                    }
                                    deleteIcon={<CancelIcon onMouseDown={(event) => event.stopPropagation()}/>}
                                    onDelete={(e) => deleteSelectedSpecialization(specializationName)}
                                    clickable
                                />
                            ))}
                        </Box>
                    )}
                    MenuProps={MenuProps}
                >
                    {specializations.map((specialization) => (
                        <MenuItem key={specialization.id} value={specialization.name}>
                            {specialization.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {specializations.length === 0 && (
                <Alert severity="info" sx={{mb: 2, width: 400}}>
                    Специализации можно подгрузить из МИС в разделе "Врачи" → "Специализации"
                </Alert>
            )}

            <FormControl sx={{mb: 1, width: 400}}>
                <InputLabel id='categoriesLabel'>Категории, в которых отображается врач</InputLabel>
                <Select
                    labelId='categoriesLabel'
                    multiple
                    fullWidth
                    value={selectedCategoryIds}
                    onChange={handleSelectCategories}
                    input={<OutlinedInput id='select-multiple-chip' label='Категории, в которых отображается врач'/>}
                    renderValue={(selected) => (
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                            {selected.map((categoryId) => {
                                const category = categories?.find((cat) => cat.id === categoryId);
                                return category ? (
                                    <Chip
                                        key={categoryId}
                                        label={category.name}
                                        deleteIcon={<CancelIcon onMouseDown={(event) => event.stopPropagation()}/>}
                                        onDelete={(e) => deleteSelectedCategory(categoryId)}
                                        clickable
                                    />
                                ) : null;
                            })}
                        </Box>
                    )}
                    MenuProps={MenuProps}
                >
                    {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                            {category.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Button
                variant='contained'
                disabled={
                    !doctor.name ||
                    !doctor.description ||
                    selectedBranchesNames.length === 0 ||
                    selectedSpecializationsNames.length === 0 ||
                    linkError ||
                    appointmentLinkError
                }
                sx={{mb: 1}}
                onClick={saveDoctor}
                disableElevation={true}
            >
                Сохранить
            </Button>
            {!isNew && (
                <Button
                    variant='contained'
                    color='error'
                    sx={{mb: 1}}
                    onClick={removeDoctor}
                    disableElevation={true}
                >
                    Удалить
                </Button>
            )}
                </>
            )}
        </Container>
    );
};

export default Doctor;
